import { api } from '@goaltree/backend/convex/_generated/api'
import type { Doc } from '@goaltree/backend/convex/_generated/dataModel'
import { NEWSLETTER_DEFAULT_SUBSCRIBED } from '@goaltree/backend/convex/lib/newsletter'
import { DEFAULT_QUIET_END, DEFAULT_QUIET_START } from '@goaltree/backend/convex/lib/pushGate'
import { Popover, PopoverContent, PopoverTrigger } from '@goaltree/ui/components/popover'
import { cn } from '@goaltree/ui/lib/utils'
import { useQuery } from 'convex/react'
import { Mail } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useUpdatePreferences } from '@/hooks/use-update-preferences'
import { useWebPush } from '@/hooks/use-web-push'

import {
  HourSelect,
  SettingsGroup,
  SettingsRow,
  SettingsSection,
  Toggle,
  formatHour,
  range,
  rowId,
} from './primitives'

type Prefs = Partial<Doc<'userPreferences'>>
type NotifField =
  | 'notifGoalReminders'
  | 'notifHabitReminders'
  | 'notifStreakAlerts'
  | 'notifWeeklyReview'
  | 'notifWeeklyDigest'
  | 'notifHabitIdeas'
  | 'notifVisualise'
  | 'notifReplan'
  | 'notifProductUpdates'
  | 'newsletterOptIn'
  | 'coachDigestEmail'

// Mirrors the backend DEFAULTS (preferences.ts).
const NOTIF_DEFAULTS: Record<NotifField, boolean> = {
  notifGoalReminders: true,
  notifHabitReminders: true,
  notifStreakAlerts: true,
  notifWeeklyReview: true,
  notifWeeklyDigest: true,
  notifHabitIdeas: true,
  notifVisualise: false,
  notifReplan: true,
  notifProductUpdates: false,
  newsletterOptIn: NEWSLETTER_DEFAULT_SUBSCRIBED,
  coachDigestEmail: true,
}

export const NOTIF_GROUPS: {
  group: 'daily' | 'weekly' | 'ideas' | 'news'
  rows: { field: NotifField; key: string }[]
}[] = [
  {
    group: 'daily',
    rows: [
      { field: 'notifGoalReminders', key: 'briefing' },
      { field: 'notifHabitReminders', key: 'habitReminders' },
      { field: 'notifStreakAlerts', key: 'streak' },
    ],
  },
  {
    group: 'weekly',
    rows: [
      { field: 'notifWeeklyReview', key: 'weeklyReview' },
      { field: 'notifWeeklyDigest', key: 'weeklyDigest' },
    ],
  },
  {
    group: 'ideas',
    rows: [
      { field: 'notifHabitIdeas', key: 'habitIdeas' },
      { field: 'notifVisualise', key: 'visualise' },
      { field: 'notifReplan', key: 'replan' },
    ],
  },
  {
    group: 'news',
    rows: [
      { field: 'notifProductUpdates', key: 'productUpdates' },
      { field: 'newsletterOptIn', key: 'newsletter' },
    ],
  },
]

const notifOn = (prefs: Prefs | undefined, field: NotifField) =>
  prefs?.[field] ?? NOTIF_DEFAULTS[field]

export function useNotifSummary() {
  const { t } = useTranslation()
  const prefs = useQuery(api.preferences.getMyPreferences) as Prefs | undefined
  if (!prefs) return undefined
  if ((prefs.notifPausedUntil ?? 0) > Date.now()) return t('settings.paused')
  const count = NOTIF_GROUPS.flatMap((g) => g.rows).filter((r) => notifOn(prefs, r.field)).length
  return t('settings.notifOn', { count })
}

const chip =
  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium'
const chipTone = {
  ok: 'border-emerald-500/35 text-emerald-600 dark:text-emerald-400',
  warn: 'cursor-pointer border-amber-500/40 text-amber-600 dark:text-amber-400',
  mute: 'border-border text-muted-foreground',
}

function ChannelChips({ prefs }: { prefs: Prefs | undefined }) {
  const { t } = useTranslation()
  const { supported, permission, isSubscribed, isLoading, subscribe, unsubscribe } = useWebPush()
  const emailOn = notifOn(prefs, 'notifWeeklyDigest') || notifOn(prefs, 'notifWeeklyReview')

  async function turnOn() {
    if (!(await subscribe()) && Notification.permission === 'denied')
      toast.error(t('settings.pushDenied'))
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {supported &&
        (permission === 'denied' ? (
          <Popover>
            <PopoverTrigger className={cn(chip, chipTone.warn)}>
              ● {t('settings.chanPushBlocked')}
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 text-xs text-muted-foreground">
              {t('settings.pushFixWeb')}
            </PopoverContent>
          </Popover>
        ) : isSubscribed ? (
          <Popover>
            <PopoverTrigger className={cn(chip, chipTone.ok, 'cursor-pointer')}>
              ● {t('settings.chanPush')}
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64 space-y-2 text-xs text-muted-foreground">
              <p>{t('settings.pushThisDeviceDesc')}</p>
              <button
                type="button"
                disabled={isLoading}
                onClick={() => void unsubscribe()}
                className="w-full cursor-pointer rounded-lg border border-border px-3 py-1.5 font-semibold text-foreground transition-colors hover:bg-muted disabled:opacity-50"
              >
                {t('settings.pushTurnOffHere')}
              </button>
            </PopoverContent>
          </Popover>
        ) : (
          <button
            type="button"
            disabled={isLoading}
            onClick={turnOn}
            className={cn(chip, chipTone.mute, 'cursor-pointer hover:text-foreground')}
          >
            ○ {t('settings.chanPushOff')} ·{' '}
            <b className="text-primary-text">{t('settings.turnOn')}</b>
          </button>
        ))}
      <span className={cn(chip, emailOn ? chipTone.ok : chipTone.mute)}>
        <Mail className="size-3 shrink-0" />
        {emailOn ? t('settings.chanEmail') : t('settings.chanEmailOff')}
      </span>
    </div>
  )
}

const SUNDAY = new Date(2024, 0, 7)

export function NotificationsSection() {
  const { t, i18n } = useTranslation()
  const prefs = useQuery(api.preferences.getMyPreferences) as Prefs | undefined
  const updatePrefs = useUpdatePreferences()
  const isCoach = (useQuery(api.coaching.myClientCount) ?? 0) > 0
  const loading = !prefs

  function save(patch: Parameters<typeof updatePrefs>[0]) {
    updatePrefs(patch).catch(() => toast.error(t('settings.prefError')))
  }

  const timeFor: Partial<Record<NotifField, React.ReactNode>> = {
    notifGoalReminders: (
      <HourSelect
        value={prefs?.notifGoalRemindersHour ?? 7}
        hours={range(5, 11)}
        onChange={(h) => save({ notifGoalRemindersHour: h })}
        disabled={loading}
      />
    ),
    notifWeeklyReview: (
      <HourSelect
        value={prefs?.notifWeeklyReviewHour ?? 20}
        hours={range(17, 22)}
        prefix={SUNDAY.toLocaleDateString(i18n.language, { weekday: 'short' })}
        onChange={(h) => save({ notifWeeklyReviewHour: h })}
        disabled={loading}
      />
    ),
    notifVisualise: (
      <span className="shrink-0 rounded-lg border px-2 py-1 font-mono text-xs text-muted-foreground">
        {formatHour(8, i18n.language)}
      </span>
    ),
  }

  const quietOn = !!prefs?.quietHoursOn

  return (
    <SettingsSection id="notifications" title={t('settings.sec.notifications')}>
      <ChannelChips prefs={prefs} />
      {NOTIF_GROUPS.map(({ group, rows }) => (
        <div key={group}>
          <SettingsGroup label={t(`settings.group.${group}`)}>
            {[
              ...rows,
              ...(group === 'weekly' && isCoach
                ? [{ field: 'coachDigestEmail' as const, key: 'coachDigest' }]
                : []),
            ].map(({ field, key }) => {
              const on = notifOn(prefs, field)
              return (
                <SettingsRow
                  key={field}
                  id={rowId(`settings.label.${key}`)}
                  label={t(`settings.label.${key}`)}
                  example={t(`settings.ex.${key}`)}
                >
                  <div className="flex items-center gap-2">
                    {on && timeFor[field]}
                    <Toggle
                      label={t(`settings.label.${key}`)}
                      checked={on}
                      onChange={(v) => save({ [field]: v })}
                      disabled={loading}
                    />
                  </div>
                </SettingsRow>
              )
            })}
          </SettingsGroup>
          {group === 'ideas' && (
            <div className="mt-4">
              <SettingsGroup label={t('settings.group.quiet')}>
                <SettingsRow
                  id={rowId('settings.quietLabel')}
                  label={t('settings.quietLabel')}
                  description={t('settings.quietDesc')}
                >
                  <div className="flex flex-wrap items-center justify-end gap-1.5">
                    {quietOn && (
                      <>
                        <HourSelect
                          value={prefs?.quietHoursStart ?? DEFAULT_QUIET_START}
                          hours={range(0, 23)}
                          onChange={(h) => save({ quietHoursStart: h })}
                        />
                        <span className="text-muted-foreground/60">–</span>
                        <HourSelect
                          value={prefs?.quietHoursEnd ?? DEFAULT_QUIET_END}
                          hours={range(0, 23)}
                          onChange={(h) => save({ quietHoursEnd: h })}
                        />
                      </>
                    )}
                    <Toggle
                      label={t('settings.quietLabel')}
                      checked={quietOn}
                      onChange={(v) => save({ quietHoursOn: v })}
                      disabled={loading}
                    />
                  </div>
                </SettingsRow>
              </SettingsGroup>
            </div>
          )}
        </div>
      ))}
    </SettingsSection>
  )
}
